import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@brevalt/ui";
import { isApiError } from "@brevalt/api-client";

/**
 * Shows a failed call as an error toast: the problem `detail` (a sentence for the user from the API; API text is
 * not in en.json by design, HANDOFF §0.9) and the request id as the reference for support.
 *
 * The same function in every language (the instance's `t`, read when the toast shows): the apps build their query
 * client on it, and a new client on a language switch dropped the cache and re-ran the effects that hold it (the
 * profile's old locale came back, L1d).
 */
export function useErrorToast(): (error: unknown) => void {
  const toast = useToast();
  const { i18n } = useTranslation();
  return useCallback(
    (error: unknown) => {
      const detail = isApiError(error) ? error.problem.detail : undefined;
      const requestId = isApiError(error) ? error.requestId : undefined;
      toast({
        tone: "error",
        title: detail ?? i18n.t("misc.something_went_wrong_on_our_side"),
        description: requestId ? `${i18n.t("misc.if_it_keeps_happening_send_us")} ${requestId}` : undefined,
      });
    },
    [toast, i18n],
  );
}
